import React, { useState } from 'react';
import { Settings, UserPlus, Pencil, Check, X, Users, Mail, Copy, Palette } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { WorkspaceIconPicker, WorkspaceIcon } from './WorkspaceIcons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

export function WorkspaceSettings() {
  const { currentWorkspace, updateWorkspace, inviteToWorkspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState('');

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open && currentWorkspace) {
      setNewName(currentWorkspace.name);
      setSelectedIcon(currentWorkspace.icon || 'bolt');
    }
    if (!open) {
      setIsEditing(false);
      setInviteEmail('');
      setInviteResult(null);
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim() || !currentWorkspace) return;
    
    try {
      await updateWorkspace(currentWorkspace.id, { name: newName.trim() });
      setIsEditing(false);
      toast.success('Workspace uppdaterat');
    } catch (error) {
      toast.error('Kunde inte uppdatera workspace');
    }
  };

  const handleIconSelect = async (iconId) => {
    setSelectedIcon(iconId);
    if (currentWorkspace) {
      try {
        await updateWorkspace(currentWorkspace.id, { icon: iconId });
        toast.success('Ikon uppdaterad');
      } catch (error) {
        toast.error('Kunde inte uppdatera ikon');
      }
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !currentWorkspace) return;
    
    setInviteLoading(true);
    setInviteResult(null);
    
    try {
      const result = await inviteToWorkspace(currentWorkspace.id, inviteEmail.trim());
      setInviteResult(result);
      setInviteEmail('');
      
      if (result.email_sent) {
        toast.success(`Inbjudan skickad till ${result.invited_email}`);
      } else {
        toast.info('Inbjudan skapad - kopiera länken nedan');
      }
    } catch (error) {
      toast.error('Kunde inte skicka inbjudan');
    } finally {
      setInviteLoading(false);
    }
  };

  const copyInviteLink = () => {
    if (inviteResult?.invite_link) {
      navigator.clipboard.writeText(inviteResult.invite_link);
      toast.success('Länk kopierad!');
    }
  };

  if (!currentWorkspace) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          className="p-2 rounded-lg hover:bg-white/5 transition-colors text-zinc-500 hover:text-zinc-300 relative z-10"
          data-testid="workspace-settings-btn"
        >
          <Settings className="w-4 h-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#141416] border-white/10 text-zinc-50 max-w-md" data-testid="workspace-settings-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium font-['Outfit']">
            Workspace-inställningar
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Workspace Name */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-sm flex items-center gap-2">
              <Pencil className="w-3 h-3" />
              Workspace-namn
            </Label>
            
            {isEditing ? (
              <div className="flex gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="bg-[#09090B] border-white/10 text-zinc-100 flex-1"
                  data-testid="workspace-name-input"
                  autoFocus
                />
                <Button
                  size="icon"
                  onClick={handleSaveName}
                  className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                  data-testid="workspace-name-save"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setNewName(currentWorkspace.name);
                  }}
                  className="border-white/10 text-zinc-400 hover:bg-white/5"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#09090B] border border-white/10">
                <span className="text-zinc-100">{currentWorkspace.name}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  className="text-zinc-500 hover:text-zinc-100 hover:bg-white/5"
                  data-testid="workspace-name-edit"
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Ändra
                </Button>
              </div>
            )}
          </div>

          {/* Workspace Icon */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-sm flex items-center gap-2">
              <Palette className="w-3 h-3" />
              Workspace-ikon
            </Label>
            <WorkspaceIconPicker selected={selectedIcon} onSelect={handleIconSelect} />
          </div>

          {/* Members */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-sm flex items-center gap-2">
              <Users className="w-3 h-3" />
              Medlemmar ({(currentWorkspace.members?.length || 0) + 1})
            </Label>
            
            <div className="space-y-2">
              {/* Owner */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#09090B] border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs text-zinc-300">
                    {currentWorkspace.owner_name?.[0] || 'O'}
                  </div>
                  <div>
                    <p className="text-sm text-zinc-100">{currentWorkspace.owner_name || 'Ägare'}</p>
                    <p className="text-xs text-zinc-500">Ägare</p>
                  </div>
                </div>
              </div>

              {/* Other Members */}
              {currentWorkspace.members?.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-3 rounded-lg bg-[#09090B] border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs text-zinc-300">
                      {member.name?.[0] || 'M'}
                    </div>
                    <div>
                      <p className="text-sm text-zinc-100">{member.name}</p>
                      <p className="text-xs text-zinc-500">{member.email}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Invite */}
          <div className="space-y-3">
            <Label className="text-zinc-400 text-sm flex items-center gap-2">
              <UserPlus className="w-3 h-3" />
              Bjud in medlem
            </Label>
            
            <form onSubmit={handleInvite} className="flex gap-2">
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                className="bg-[#09090B] border-white/10 text-zinc-100 flex-1 placeholder:text-zinc-600"
                data-testid="invite-email-input"
              />
              <Button
                type="submit"
                disabled={inviteLoading || !inviteEmail.trim()}
                className="bg-zinc-100 text-zinc-900 hover:bg-white"
                data-testid="invite-submit-btn"
              >
                {inviteLoading ? (
                  <span className="w-4 h-4 border-2 border-zinc-900/20 border-t-zinc-900 rounded-full animate-spin" />
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-1" />
                    Bjud in
                  </>
                )}
              </Button>
            </form>

            {/* Invite Result */}
            {inviteResult?.invite_link && (
              <div className="p-3 rounded-lg bg-zinc-800/50 border border-white/5 space-y-2">
                <p className="text-xs text-zinc-400">
                  {inviteResult.email_sent ? 'Inbjudan skickad! Dela även länken direkt:' : 'Kopiera länken och skicka manuellt:'}
                </p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={inviteResult.invite_link}
                    className="bg-[#09090B] border-white/10 text-zinc-300 text-xs flex-1"
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={copyInviteLink}
                    className="border-white/10 text-zinc-400 hover:bg-white/5"
                    data-testid="copy-invite-link"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
